import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import cn from "classnames";
import { request } from "../../functions/apiRequestHelper";
import {Formik, Form} from "formik";
import queryString from "query-string";
import { validate } from "../../functions/validation";
import { gtmPageview, gtmPush } from "../../functions/gtm";
import Layout from "../../components/Layout";
import Spinner from "../../audi-ui-components/Spinner";
import Checkbox from "../../audi-ui-components/Checkbox";
import Button from "../../audi-ui-components/Button";
import StepVin from "./StepVin";
import StepReason from "./StepReason";
import StepUserDetails from "./StepUserDetails";
import StepNewOwnerDetails from "./StepNewOwnerDetails";
import StepDate from "./StepDate";
import StepDocs from "./StepDocs";
import StepConsent from "./StepConsent";
import SubmittedBanner from "./SubmittedBanner";
import Summary from "./Summary";
import NextSteps from "./NextSteps";
import {
  PATH_CHANGE_VEHICLE_OWNER,
  PATH_THANKS,
  UI_AUDI,
} from "../../constants";
import {
  requestTypes,
  validationSchema,
  initialValues,
  mapValues,
  mapApiErrors,
} from "./CVOConstants";
import "./cvo.scss";

// test: WAUZZZGU2S2026596

const ChangeVehicleOwner = ({ history, location }) => {
  //
  const query = queryString.parse(location.search);
  const [step, setStep] = useState(1);
  const [vehicle, setVehicle] = useState({});
  const [vinImg, setVinImg] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState({requestType: requestTypes.WRITTEN_OFF});
  const [serverError, setServerError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingImg, setIsLoadingImg] = useState(false);

  const handleSubmit = async (values, formikBag) => {
    setServerError(null);
    let data = {
      formName: "changeownership",
      payload: mapValues(values),
    };
    let fd = new FormData();
    fd.append("data", JSON.stringify(data));
    if (values.files1?.name) {
      fd.append("files", values.files1);
      if (values.files2?.name) {
        fd.append("files", values.files2);
      }
    }
    if (process.env.RAZZLE_APP_ENV !== "local" || values.submitToBackend) {
      // submit to backend
      try {
        const response = await fetch(`${process.env.RAZZLE_API_HOST}/1/form/changeOwnership`, {
          method: 'POST',
          body: fd
        });
        if (response.ok) {
          // success
          formikBag.setSubmitting(false);
          gtmPush("form", "formSubmitSuccess", "changeOwnership", data.payload);
          setSubmitted(true);
          setSubmittedData(values);
          history.push(`${PATH_CHANGE_VEHICLE_OWNER}${PATH_THANKS}`);
          gtmPageview(`${PATH_CHANGE_VEHICLE_OWNER}${PATH_THANKS}`);
        } else {
          // fail
          let contentType = response.headers.get('Content-Type') || '';
          if (contentType?.includes('application/json')) {
            // json response
            const error = await response.json();
            // console.log(error);
            if (error?.modelState) {
              let errors = mapApiErrors(error.modelState);
              formikBag.setErrors(errors);
              // go to step
              if (errors.vin) { setStep(1); }
              else if (errors.phone || errors.email) { setStep(3); }
              else if (errors.newOwnerPhone || errors.newOwnerEmail) { setStep(4); }
              // scroll to first error
              setTimeout(() => {
                let el = document.querySelector(".is-invalid");
                if (el) {
                  el.scrollIntoView({behavior: "smooth", block: "center"});
                }
              }, 1000);
            } else {
              var msg = "An error has occured";
              if (error?.message) {
                msg = error.message;
              }
              setServerError(msg);
            }
          } else {
            // text response
            const resultText = await response.text();
            let msg = `Error ${response.status}: ${resultText}`;
            setServerError(msg);
          }
          formikBag.setSubmitting(false);
        }
      } catch (error) {
        // fail
        formikBag.setSubmitting(false);
        setServerError(error.message);
      }
    } else {
      // fake submit for frontend testing
      setSubmitted(true);
      setSubmittedData(values);
      history.push(`${PATH_CHANGE_VEHICLE_OWNER}${PATH_THANKS}`);
    }
  }

  const validateForm = (values) => {
    var v = validate(values, validationSchema, {format: "firstError", fullMessages: false});
    // console.log(v);
    return v;
  }

  const onSubmitVin = (values, errors, setFieldValue, setFieldTouched) => {
    setServerError(null);
    setFieldTouched('vin');
    setFieldTouched('rego');
    setFieldTouched('regoState');
    if (values.vin && !errors.vin) {
      lookupVin(values.vin, values, setFieldValue);
    } else if (values.rego && values.regoState) {
      lookupRego(values, setFieldValue);
    }
  };
  const lookupVin = (vin, values, setFieldValue) => {
    setIsLoading(true);
    request(
      `${process.env.RAZZLE_API_HOST}/2/services/vehicle/${vin}`,
      {
        method: 'POST',
      }
    ).then(response => {
      // success
      if (response.vin) {
        if (!values.vin) {
          setFieldValue("vin", vin);
        }
        if (!values.rego) {
          setFieldValue("rego", response.rego);
          setFieldValue("regoState", response.state);
        }
        // lookup redbook data
        request(
          `${process.env.RAZZLE_REDBOOK_API}/1/redbook/audi/vehicle/vin/${vin}/details`,
          {
            method: 'POST',
          }
        ).then(rbResponse => {
          // success
          if (rbResponse.make) {
            let data = {...response};
            data.redbook = rbResponse;
            setVehicle(data);
            setIsLoading(false);
            setStep(2);
          } else {
            setVehicle(response);
            setIsLoading(false);
            setStep(2);
          }
        }).catch(error => {
          // error
          setVehicle(response);
          setIsLoading(false);
          setStep(2);
        });
        // end lookup redbook data
      } else {
        console.log(response);
        setServerError("We couldn't find that vehicle");
        setIsLoading(false);
      }
    }).catch(error => {
      // error
      console.log(error);
      setServerError("We couldn't find that vehicle");
      setIsLoading(false);
    });
  }
  const lookupRego = (values, setFieldValue) => {
    setIsLoading(true);
    request(
      `${process.env.RAZZLE_API_HOST}/2/services/vehicle/${values.rego}/${values.regoState}`,
      {
        method: 'POST',
      }
    ).then(response => {
      // success
      if (response.vin) {
        lookupVin(response.vin, values, setFieldValue);
      } else {
        setIsLoading(false);
        setServerError("We can't find a matching Audi VIN with the registration number you have entered. Please try entering the VIN directly.");
      }
    }).catch(error => {
      // error
      console.log(error);
      setServerError("We can't find a matching Audi VIN with the registration number you have entered. Please try entering the VIN directly.");
      setIsLoading(false);
    });
  }

  const getVehicleName = (vehicle) => {
    let y = vehicle.redbook?.year || vehicle.vehicleAge?.yearOfManufacturer;
    if (y === 0 || y === "0") {
      y = "";
    }
    let mk = vehicle.redbook?.make || vehicle.vehicleData?.make || vehicle.vehicleData?.makeDescription;
    if (mk === "AUD") {
      mk = "AUDI";
    }
    let md = vehicle.redbook?.family || vehicle.vehicleData?.model || vehicle.vehicleData.modelDescription;
    return y ? `${y} ${mk} ${md}` : `${mk} ${md}`;
  }

  useEffect(() => {
    gtmPageview(PATH_CHANGE_VEHICLE_OWNER);
  }, []);

  useEffect(() => {
    if (vehicle.vin) {
      setIsLoadingImg(true);
      request(
        `${process.env.RAZZLE_MEDIASERVICE}/MediaServiceCache?vin=${vehicle.vin}&view=n3c14aut&mimeType=image/png&wid=400`,
        {}
      ).then((res) => {
        // console.log(res);
        setVinImg(res.imageUrl);
        setIsLoadingImg(false);
      }).catch((error) => {
        setIsLoadingImg(false);
      });
    } else {
      setVinImg(null);
    }
  }, [vehicle.vin]);

  return (
    <Layout ui={query.ui || UI_AUDI} embedded={query.embedded} theme={query.theme}>
      <Helmet>
        <title>Change Vehicle Ownership | Audi Australia</title>
      </Helmet>

      {submitted && <>
        <SubmittedBanner requestType={submittedData.requestType} />
        <main className="audi-forms__page cvo">
          <Summary submittedData={submittedData} />
          <NextSteps requestType={submittedData.requestType} />
        </main>
      </>}

      {!submitted && <main className="audi-forms__page cvo">
        <div className="row">
          <div className="col-5 d-none d-medium-block">
            <div className="audi-forms-side-hero" style={{backgroundImage: `url("${process.env.PUBLIC_PATH}img/cvo-banner.jpg")`}}>
            </div>
          </div>

          <div className="col-12 col-medium">
            <div className="px-0 px-small-3 px-large-5 py-3 py-medium-5 h-100">
              {step === 1 && <div className="mb-3 d-medium-none"><img src={`${process.env.PUBLIC_PATH}img/cvo-banner_m.jpg`} alt="" style={{width: "100%"}} /></div>}
              <Formik onSubmit={handleSubmit} initialValues={initialValues} validate={validateForm} validateOnMount>
                {(formikProps) => (
                  <Form className="audi-form">
                    {(formikProps.isSubmitting || isLoading) && <div className="spinner-wrapper"><Spinner isContinuous /></div>}

                    <p className={cn("mb-4", {"aui-headline-2": step === 1, "aui-headline-3": step > 1})}>Change ownership details</p>

                    {vehicle?.vin && step !== 1 && <div className="vehicle mb-5">
                      <div>
                        <p className="vehicle-heading">Your Audi</p>
                        <p><b>{getVehicleName(vehicle)}</b></p>
                        <p>{vehicle.rego} ({vehicle.state})</p>
                        <p>{vehicle.vin}</p>
                      </div>
                      <div className="vehicle-img">
                        {vinImg && !isLoadingImg && <img src={vinImg} alt="" />}
                        {isLoadingImg && <Spinner isContinuous />}
                      </div>
                    </div>}

                    <div className="form-step">
                      <div className={cn({"d-none": step !== 1})}>
                        <StepVin {...formikProps} theme={query.theme} />
                      </div>

                      <div className={cn({"d-none": step !== 2})}>
                        <StepReason {...formikProps} theme={query.theme} />
                      </div>

                      <div className={cn({"d-none": step !== 3})}>
                        <StepUserDetails {...formikProps} theme={query.theme} />
                      </div>

                      <div className={cn({"d-none": step !== 4})}>
                        <StepNewOwnerDetails {...formikProps} theme={query.theme} />
                      </div>

                      <div className={cn({"d-none": step !== 5})}>
                        <StepDate {...formikProps} theme={query.theme} />
                        <StepDocs {...formikProps} theme={query.theme} />
                        <StepConsent {...formikProps} theme={query.theme} />
                      </div>

                      <div className="form-actions">
                        {serverError && <p className="server-error">{serverError}</p>}
                        {process.env.RAZZLE_APP_ENV === 'local' && <div className="form-field dev-only">
                          <Checkbox name="submitToBackend" label="Submit to backend" checked={!!formikProps.values.submitToBackend} value="" onChange={(v,e) => { formikProps.setFieldValue('submitToBackend', e.target.checked); }} />
                        </div>}
                        {step > 1 && <Button variant="secondary" onClick={() => {
                          if (step === 5 && formikProps.values.requestType !== requestTypes.NO_LONGER_OWN) {
                            setStep(3); // skip New Owner details
                          } else {
                            setStep(step - 1);
                          }
                          gtmPush("cvoBackButton", "ChangeVehicleOwnership");
                        }}>Back</Button>}

                        {step < 5 && (
                          <Button
                            variant="primary"
                            onClick={() => {
                              if (step === 1) {
                                onSubmitVin(formikProps.values, formikProps.errors, formikProps.setFieldValue, formikProps.setFieldTouched);
                              } else if (step === 2) {
                                formikProps.setFieldTouched("requestType");
                                if (!formikProps.errors.requestType) { setStep(3); }
                              } else if (step === 3) {
                                let fieldKeys = ["firstName", "lastName", "email", "phone", "address", "suburb", "postCode", "changedName", "changedPhone", "changedEmail", "changedAddress"];
                                let errorKeys = Object.keys(formikProps.errors);
                                let intersection = fieldKeys.filter(x => errorKeys.includes(x)) || [];
                                formikProps.setTouched(Object.fromEntries(fieldKeys.map(k => [k, true])));
                                if (intersection.length === 0) {
                                  if (formikProps.values.requestType !== requestTypes.NO_LONGER_OWN) {
                                    setStep(5); // skip New Owner details
                                  } else {
                                    setStep(4);
                                  }
                                }
                              } else if (step === 4) {
                                formikProps.setTouched({"newOwnerType": true, "dealerName": true});
                                if (!formikProps.errors.newOwnerType && !formikProps.errors.dealerName) {
                                  setStep(5);
                                }
                              }
                              gtmPush("cvoNextButton", "ChangeVehicleOwnership");
                            }}
                          >Next</Button>
                        )}

                        {step === 5 && <Button
                          variant="primary"
                          type="submit"
                        >Submit</Button>}
                      </div>

                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </main>}
    </Layout>
  );
}

export default ChangeVehicleOwner;
